import type { Program } from "@coral-xyz/anchor";
import type { Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import type { StepSubscriptions } from "@stepfinance/common";

import type { TiersNotificationStrings } from "../../../contexts/language/languageTypes";
import type { ConnectedWallet } from "../../../hooks";
import { sendTransaction } from "../../../utils/transactions";
import { findAccountByMint } from "../../account";
import { GLOBAL_STATE_SEED, PRODUCT_SEED, PURCHASE_SEED } from "./constants";

export const acquireTier = async (
  program: Program<StepSubscriptions>,
  connection: Connection,
  // user's public key
  wallet: ConnectedWallet,
  tierId: string,
  paymentMint: string,
  translations: TiersNotificationStrings,
): Promise<string> => {
  const fromAccount = wallet.publicKey;
  const productKey = PublicKey.findProgramAddressSync(
    [Buffer.from(PRODUCT_SEED), Buffer.from(tierId)],
    program.programId,
  )[0];
  const globalStateKey = PublicKey.findProgramAddressSync([Buffer.from(GLOBAL_STATE_SEED)], program.programId)[0];

  const globalState = await program.account.globalState.fetch(globalStateKey);
  const selectedUserTokenAccount = await findAccountByMint(connection, fromAccount, new PublicKey(paymentMint));

  const selectedToPayTokenAccount = await findAccountByMint(
    connection,
    globalState.paymentWallet,
    new PublicKey(paymentMint),
  );

  const purchaseIx = await program.methods
    .purchaseProduct(1)
    .accounts({
      program: program.programId,
      regarding: fromAccount,
      product: productKey,
      paymentFromTokenAccount: selectedUserTokenAccount,
      paymentFromTokenAuthority: fromAccount,
      paymentToTokenAccount: selectedToPayTokenAccount,
    })
    .instruction();

  return await sendTransaction(connection, wallet, [purchaseIx], [], true, undefined, true, {
    success: {
      message: translations.successfulPurchase,
      description: translations.newTierBought,
    },
  });
};

export const upgradeTier = async (
  program: Program<StepSubscriptions>,
  connection: Connection,
  wallet: ConnectedWallet,
  tierToUpgradeId: string,
  paymentMint: string,
  currentTierId: string,
  translations: TiersNotificationStrings,
): Promise<string> => {
  const fromAccount = wallet.publicKey;

  const currentProductKey = PublicKey.findProgramAddressSync(
    [Buffer.from(PRODUCT_SEED), Buffer.from(currentTierId)],
    program.programId,
  )[0];

  const currentPurchaseKey = PublicKey.findProgramAddressSync(
    [Buffer.from(PURCHASE_SEED), fromAccount.toBuffer(), currentProductKey.toBuffer(), fromAccount.toBuffer()],
    program.programId,
  )[0];

  const tierToUpgradeKey = PublicKey.findProgramAddressSync(
    [Buffer.from(PRODUCT_SEED), Buffer.from(tierToUpgradeId)],
    program.programId,
  )[0];

  const selectedUserTokenAccount = await findAccountByMint(connection, fromAccount, new PublicKey(paymentMint));

  const globalStateKey = PublicKey.findProgramAddressSync([Buffer.from(GLOBAL_STATE_SEED)], program.programId)[0];
  const globalState = await program.account.globalState.fetch(globalStateKey);
  const selectedToPayTokenAccount = await findAccountByMint(
    connection,
    globalState.paymentWallet,
    new PublicKey(paymentMint),
  );

  const purchaseIx = await program.methods
    .purchaseProduct(1)
    .accounts({
      program: program.programId,
      regarding: fromAccount,
      product: tierToUpgradeKey,
      paymentFromTokenAccount: selectedUserTokenAccount,
      paymentFromTokenAuthority: fromAccount,
      paymentToTokenAccount: selectedToPayTokenAccount,
    })
    .remainingAccounts([
      {
        pubkey: currentProductKey,
        isWritable: false,
        isSigner: false,
      },
      {
        pubkey: currentPurchaseKey,
        isWritable: true,
        isSigner: false,
      },
    ])
    .instruction();

  return await sendTransaction(connection, wallet, [purchaseIx], [], true, undefined, true, {
    success: {
      message: translations.successfulUpgrade,
      description: translations.tierUpgraded,
    },
  });
};
